import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { PaymentMethod } from '@prisma/client';
import FullPageModal from '../modal/FullPageModal';
import ClientSearchAutofill from '../form/ClientSearchAutofill';
import CheckoutDetails from '../form/CheckoutDetails';
import CheckoutFormItems from '../form/CheckoutFormItems';
import { getServiceName } from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import PaymentMethodsList from './PaymentMethodsList';
import {
  AppointmentDepositCheckoutSession,
  CheckoutPaymentMethod,
  DepositCheckoutFormValues,
} from '../../types/appointments';
import { formatDollarAmount } from '../../utils/global/helper';
import CheckoutFormPricingTable from '../form/CheckoutFormPricingTable';
import ValuesListener from '../form/ValuesListener';
import useDepositCheckoutSession from '../../hooks/appointments/useDepositCheckoutSession';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import {
  getAppointmentDepositCheckoutSession,
  markDepositPaid,
  invalidateAppointment,
} from '../../services/appointments';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import useDepositCheckoutForm from '../../hooks/global/useDepositCheckoutForm';
import useGetAppointment from '../../hooks/appointments/useGetAppointment';
import useTerminalReader from '../../hooks/businesses/useTerminalReader';
import { updateCustomer } from '../../services/user';
import { searchClient } from '../../algoliaconfig';
import useCashDiscount from '../../hooks/payments/useCashDiscount';
import { useDepositPriceFormModal } from '../../hooks/checkouts/useDepositPriceFormModal';
import { Appointment } from '../../interfaces';
import { getDepositPricingRows } from '../../helpers/payments/depositPricing';

interface BodyProps {
  appointment: Appointment;
  availablePaymentMethods: CheckoutPaymentMethod[];
  displaySession: AppointmentDepositCheckoutSession | undefined | null;
  checkoutSession: AppointmentDepositCheckoutSession | undefined | null;
  isLoading: boolean;
  setFormValues: (values: DepositCheckoutFormValues) => void;
}

function DepositCheckoutFormBody({
  appointment,
  availablePaymentMethods,
  displaySession,
  checkoutSession,
  isLoading,
  setFormValues,
}: BodyProps) {
  const { user } = useUser();
  const { values, setFieldValue, isSubmitting } =
    useFormikContext<DepositCheckoutFormValues>();
  const { getAllInPrice } = useCashDiscount();
  const showDepositPriceForm = useDepositPriceFormModal();

  const handleEditDeposit = React.useCallback(async () => {
    if (isSubmitting) return;
    const result = await showDepositPriceForm({
      initialPrice: values.depositPrice || 0,
      paymentMethod: values.paymentMethod,
    });
    if (!result) return;
    setFieldValue('depositPrice', Number(result.depositPrice) || 0);
  }, [
    isSubmitting,
    showDepositPriceForm,
    values.depositPrice,
    values.paymentMethod,
    setFieldValue,
  ]);

  const pricingRows = React.useMemo(
    () =>
      getDepositPricingRows(displaySession, values.paymentMethod, isLoading),
    [displaySession, values.paymentMethod, isLoading],
  );

  const items = React.useMemo(
    () => [
      {
        type: 'service' as const,
        id: appointment.appointmentId,
        name: `${getServiceName(appointment, user)} Deposit`,
        price: getAllInPrice(values.depositPrice, {
          paymentMethod: values.paymentMethod,
        }),
        disabled: isSubmitting,
        onClick: handleEditDeposit,
      },
    ],
    [
      appointment,
      user,
      values.depositPrice,
      values.paymentMethod,
      isSubmitting,
      getAllInPrice,
      handleEditDeposit,
    ],
  );

  const totalLabel =
    values.paymentMethod === 'TERMINAL'
      ? `Request ${formatDollarAmount(
          (checkoutSession?.totalPriceCents ?? 0) / 100,
          true,
        )}`
      : 'Record Payment';

  const isSubmitDisabled =
    isLoading ||
    isSubmitting ||
    !values.paymentMethod ||
    Number(values.depositPrice) <= 0 ||
    !checkoutSession;

  return (
    <>
      <ValuesListener debounceTime={250} onChange={setFormValues} />

      <ClientSearchAutofill
        title="Client & Appointment"
        hideBirthday
        namePrefix={['customer']}
        selectedRequest={appointment}
      />
      <Form style={{ paddingTop: 0 }}>
        <CheckoutDetails
          selectedAppointment={null}
          appointment={appointment}
          hideCommission={values.paymentMethod !== 'TERMINAL'}
        />
        <CheckoutFormItems items={items} />
        <CheckoutFormPricingTable {...pricingRows} />

        <div className="input-group">
          <Typography variant="h2">Payment Method</Typography>
          <PaymentMethodsList
            totalPriceCents={checkoutSession?.totalPriceCents ?? 0}
            paymentMethods={availablePaymentMethods}
            selectedPaymentMethod={
              values.paymentMethod as CheckoutPaymentMethod
            }
            disabled={isSubmitting}
            onChange={value => setFieldValue('paymentMethod', value)}
          />
        </div>

        <LoadingButton
          type="submit"
          disabled={isSubmitDisabled}
          variant="contained"
          className="primary"
          loading={isSubmitting}>
          <span>{totalLabel}</span>
        </LoadingButton>
      </Form>
    </>
  );
}

function DepositCheckoutFormContent() {
  const {
    setTerminalCheckoutModalToLoading: setTerminalCheckoutLoading,
    setMostRecentPaymentId,
    resetCheckoutFailureData,
  } = useCheckoutForm();
  const {
    depositCheckoutAppointmentId,
    closeDepositCheckout,
    onRefresh,
    allowTerminal,
  } = useDepositCheckoutForm();

  const { data: appointment } = useGetAppointment(depositCheckoutAppointmentId);
  const { data: terminal } = useTerminalReader({ enabled: allowTerminal });

  const isTerminalOnline = terminal?.status === 'online';
  const showTerminal =
    allowTerminal &&
    isTerminalOnline &&
    !appointment?.depositCheckoutSessionActive;

  const availablePaymentMethods: CheckoutPaymentMethod[] = [
    ...((showTerminal ? ['TERMINAL'] : []) as CheckoutPaymentMethod[]),
    'CASH',
    'DIGITAL',
  ];

  const initialValues = React.useMemo<DepositCheckoutFormValues>(
    () => ({
      appointmentId: appointment?.appointmentId ?? '',
      employeeId: appointment?.employeeId,
      depositPrice: appointment?.depositPrice || 0,
      paymentMethod: '',
      providerCommissionType: '',
      customer: {
        customerId: appointment?.customerId ?? '',
        firstName: appointment?.customer.firstName ?? '',
        lastName: appointment?.customer.lastName,
        email: appointment?.customer.email,
        phoneNumber: appointment?.customer.phoneNumber ?? '',
        dob: appointment?.customer.dob?.split('T')[0],
      },
    }),
    [appointment],
  );

  const [formValues, setFormValues] =
    React.useState<DepositCheckoutFormValues>(initialValues);

  const debouncedDepositPrice = useDebouncedValue(formValues.depositPrice, 250);
  const isTerminal = formValues.paymentMethod === 'TERMINAL';
  const isDebouncing = formValues.depositPrice !== debouncedDepositPrice;

  const { data: checkoutSession, isFetching } = useDepositCheckoutSession({
    appointment,
    depositPrice: Number(debouncedDepositPrice),
    status: 'schedule',
    enabled: !!appointment && Number(debouncedDepositPrice) > 0,
    isTerminal,
    providerCommissionType: isTerminal
      ? formValues.providerCommissionType
      : undefined,
  });
  const isLoading = isFetching || isDebouncing;
  const displaySession =
    formValues.depositPrice > 0 ? checkoutSession : undefined;

  const handleRefresh = React.useCallback(async () => {
    await searchClient.clearCache();
    if (onRefresh) {
      await onRefresh();
    } else {
      await invalidateAppointment(appointment?.appointmentId ?? '');
    }
  }, [appointment?.appointmentId, onRefresh]);

  const onSubmit = React.useCallback(
    async (values: DepositCheckoutFormValues) => {
      const { depositPrice, paymentMethod } = values;
      if (isLoading || !checkoutSession || depositPrice <= 0 || !appointment)
        return;

      resetCheckoutFailureData();

      if (values.customer.customerId) {
        await updateCustomer(
          {
            firstName: values.customer.firstName,
            lastName: values.customer.lastName ?? undefined,
            phoneNumber: values.customer.phoneNumber,
            email: values.customer.email ?? undefined,
            dob: values.customer.dob ?? undefined,
          },
          values.customer.customerId,
        );
      }

      if (paymentMethod === 'CASH' || paymentMethod === 'DIGITAL') {
        try {
          await markDepositPaid(appointment.appointmentId, {
            depositPaymentMethod: paymentMethod as PaymentMethod,
            depositPrice,
          });
          closeDepositCheckout();
          toast.success('Deposit marked as paid');
          await handleRefresh();
        } catch {
          toast.error('Error marking deposit as paid');
        }
        return;
      }

      // TERMINAL flow
      const {
        totalPriceCents,
        paymentIntent,
        paymentMethod: sessionPaymentMethod,
      } = await getAppointmentDepositCheckoutSession({
        appointmentId: appointment.appointmentId,
        status: 'schedule',
        depositPrice,
        isTerminal: true,
        providerCommissionType: values.providerCommissionType || undefined,
      });

      if (
        !paymentIntent ||
        totalPriceCents !== checkoutSession.totalPriceCents ||
        sessionPaymentMethod !== 'TERMINAL'
      )
        return;

      setTerminalCheckoutLoading();
      setMostRecentPaymentId(paymentIntent.paymentId);
      toast.success('Charge sent to terminal');

      closeDepositCheckout();
      await handleRefresh();
    },
    [
      appointment,
      checkoutSession,
      closeDepositCheckout,
      handleRefresh,
      isLoading,
      resetCheckoutFailureData,
      setMostRecentPaymentId,
      setTerminalCheckoutLoading,
    ],
  );

  if (!appointment) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 48,
        }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={undefined}
      onSubmit={onSubmit}>
      <DepositCheckoutFormBody
        appointment={appointment}
        availablePaymentMethods={availablePaymentMethods}
        displaySession={displaySession}
        checkoutSession={checkoutSession}
        isLoading={isLoading}
        setFormValues={setFormValues}
      />
    </Formik>
  );
}

function DepositCheckoutForm() {
  const { depositCheckoutAppointmentId, closeDepositCheckout } =
    useDepositCheckoutForm();

  const isOpen = !!depositCheckoutAppointmentId;

  return (
    <FullPageModal
      modalOpen={isOpen}
      setModalOpen={closeDepositCheckout}
      handleClose={closeDepositCheckout}
      name="Deposit Checkout">
      {isOpen && <DepositCheckoutFormContent />}
    </FullPageModal>
  );
}

export default DepositCheckoutForm;
